import { useState, useMemo, useCallback, useEffect } from 'react';
import { searchEmployeeApi, BRANCH_OPTIONS, DEPARTMENT_OPTIONS } from '../api/searchEmployeeApi';
import { appToast } from '../../../../components/AppToast';

export function useSearchEmployeeList() {
  const [branchFilter, setBranchFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'All', label: 'All Employees' },
    { value: 'Active', label: 'Active' },
    { value: 'On Leave', label: 'On Leave' },
  ];

  const searchFieldOptions = [
    { value: 'Name', label: 'Employee Name' },
    { value: 'Emp ID', label: 'Employee ID' },
    { value: 'Branch', label: 'Branch / Location' },
    { value: 'Department', label: 'Department' },
    { value: 'Mobile', label: 'Mobile Number' },
    { value: 'Email', label: 'Email ID' },
    { value: 'Designation', label: 'Designation' },
  ];

  const tools = [
    { key: 'pdf', label: 'Export Employee Directory to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Employee Directory to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Directory List', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await searchEmployeeApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Branch filter
      if (branchFilter !== 'All' && item.branch !== branchFilter) {
        return false;
      }

      // Department filter
      if (departmentFilter !== 'All' && item.department !== departmentFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Name' && !item.name?.toLowerCase().includes(query)) return false;
        if (searchField === 'Emp ID' && !item.employeeId?.includes(query) && !item.code?.toLowerCase().includes(query)) return false;
        if (searchField === 'Branch' && !item.branch?.toLowerCase().includes(query)) return false;
        if (searchField === 'Department' && !item.department?.toLowerCase().includes(query)) return false;
        if (searchField === 'Mobile' && !item.mobile?.includes(query)) return false;
        if (searchField === 'Email' && !item.email?.toLowerCase().includes(query)) return false;
        if (searchField === 'Designation' && !item.designation?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, branchFilter, departmentFilter, statusFilter, searchText, searchField]);

  // Summary statistics
  const summary = useMemo(() => {
    const totalEmployees = filteredRecords.length;
    const activeCount = filteredRecords.filter((r) => r.status === 'Active').length;
    const branches = new Set(filteredRecords.map((r) => r.branch).filter(Boolean));
    const departments = new Set(filteredRecords.map((r) => r.department).filter(Boolean));

    return {
      totalEmployees,
      activeCount,
      branchCount: branches.size,
      departmentCount: departments.size,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Employee directory filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setBranchFilter('All');
    setDepartmentFilter('All');
    setStatusFilter('All');
    setSearchText('');
    setSearchField('Name');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Employee Directory to PDF...');
    } else if (key.includes('excel')) {
      appToast.success('Exporting Employee Directory to Excel...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      branchFilter,
      departmentFilter,
      statusFilter,
      searchField,
      searchText,
      isLoading,
      branchOptions: BRANCH_OPTIONS,
      departmentOptions: DEPARTMENT_OPTIONS,
      statusOptions,
      searchFieldOptions,
      tools,
      summary,
    },
    actions: {
      setBranchFilter,
      setDepartmentFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
    },
  };
}
